/**
 * Loose time parsing for `TimeField` (calendar module). Pure functions, no React — kept
 * separate so the am/pm inference rules can be unit-tested exhaustively without mounting
 * anything.
 *
 * All "minutes" values in this file are **minutes since midnight** (0–1439) unless noted.
 *
 * ## Accepted input shapes
 *
 * - `"noon"`, `"midnight"` — literal words.
 * - Colon form: `"4:30"`, `"16:30"`, `"4:30pm"`, `"4:30 p"`.
 * - Digit-run form: `"430"` (3 digits → H:MM), `"1630"` (4 digits → HH:MM), each with an
 *   optional trailing meridiem (`"930p"`).
 * - Hour-only form: `"4"`, `"4pm"`, `"16"`.
 *
 * Meridiem suffixes accepted: `am`, `a`, `pm`, `p` (case-insensitive, optional space before).
 *
 * ## am/pm inference rules (no meridiem given, hour is 1–11)
 *
 * These are the cases a typed time is genuinely ambiguous — `"16:30"` or a 4-digit run
 * like `"1630"` is never ambiguous (hour ≥ 13 can only be 24h), and `"12"` always means
 * noon (calendaring convention — "12" alone is never read as midnight).
 *
 * 1. **With an anchor** (`anchorMinutes`, e.g. the event's start time when the field being
 *    parsed is the *end* time): pick whichever of the AM/PM reading falls strictly after
 *    the anchor, preferring the one that comes soonest after it. If neither reading is
 *    after the anchor (both would be earlier the same day), pick the later of the two —
 *    end times are assumed same-day-forward, not "yesterday". Example: start is 4:00 PM,
 *    typing `"5"` for the end infers 5:00 PM (not 5:00 AM), and typing `"3"` (both 3am and
 *    3pm are "before" 4pm) still resolves to 3:00 PM rather than jumping back to the
 *    morning.
 * 2. **Without an anchor** (e.g. the start-time field): hours 1–6 default to **PM**
 *    (afternoon/evening — the overwhelmingly common case for a bare "4" on a family
 *    calendar is "4pm", not 4am), hours 7–11 default to **AM** (the common case for a
 *    bare "9" is a morning commitment). This mirrors how people actually say these numbers
 *    out loud without a qualifier.
 *
 * A 3-digit digit-run (`"930"`) is ambiguous the same way an hour-only or colon form is —
 * the rules above apply to its parsed hour. A 4-digit run (`"1630"`, `"0930"`) is always
 * read as a literal 24-hour `HHMM` — no inference, because a leading zero or an hour ≥ 13
 * is only ever produced by someone typing in 24-hour notation.
 */

export interface ParseLooseTimeOptions {
  /** Minutes-since-midnight of a related "anchor" time (typically the event's start time,
   * when parsing its end time). Used only to disambiguate an am/pm-less 1–11 hour. */
  anchorMinutes?: number;
}

const WORD_TIMES: Record<string, number> = { noon: 12 * 60, midnight: 0 };

function inferAmPm(hour1to11: number, anchorMinutes: number | undefined): number {
  const amMinutes = (hour1to11 % 12) * 60;
  const pmMinutes = ((hour1to11 % 12) + 12) * 60;
  if (anchorMinutes === undefined) {
    return hour1to11 >= 1 && hour1to11 <= 6 ? pmMinutes : amMinutes;
  }
  const after = [amMinutes, pmMinutes].filter((m) => m > anchorMinutes).sort((a, b) => a - b);
  if (after.length > 0) return after[0] as number;
  return Math.max(amMinutes, pmMinutes);
}

function applyMeridiem(
  hour: number,
  minute: number,
  meridiem: 'am' | 'pm' | null,
  anchorMinutes: number | undefined,
): number | null {
  if (minute < 0 || minute > 59) return null;

  if (meridiem === null) {
    if (hour >= 13 && hour <= 23) return hour * 60 + minute; // unambiguous 24h
    if (hour === 0) return minute; // literal midnight hour
    if (hour === 12) return 12 * 60 + minute; // "12" alone reads as noon
    if (hour >= 1 && hour <= 11) return inferAmPm(hour, anchorMinutes) + minute;
    return null; // hour > 23
  }

  if (hour < 1 || hour > 12) return null; // explicit am/pm only makes sense for 1–12
  if (hour === 12) return (meridiem === 'am' ? 0 : 12 * 60) + minute;
  return (meridiem === 'am' ? hour : hour + 12) * 60 + minute;
}

/** Parses loose free-typed text into minutes-since-midnight, or `null` if unparseable. */
export function parseLooseTime(raw: string, options: ParseLooseTimeOptions = {}): number | null {
  const normalized = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (normalized.length === 0) return null;
  if (normalized in WORD_TIMES) return WORD_TIMES[normalized] as number;

  const meridiemMatch = /^(.*?)\s*(am|pm|a|p)$/.exec(normalized);
  const body = meridiemMatch ? (meridiemMatch[1] as string).trim() : normalized;
  const meridiem: 'am' | 'pm' | null = meridiemMatch
    ? (meridiemMatch[2] as string).startsWith('a')
      ? 'am'
      : 'pm'
    : null;
  if (body.length === 0) return null;

  const colonMatch = /^(\d{1,2}):(\d{2})$/.exec(body);
  if (colonMatch) {
    const hour = Number(colonMatch[1]);
    const minute = Number(colonMatch[2]);
    return applyMeridiem(hour, minute, meridiem, options.anchorMinutes);
  }

  const digitRunMatch = /^\d{3,4}$/.exec(body);
  if (digitRunMatch) {
    const hourLen = body.length - 2;
    const hour = Number(body.slice(0, hourLen));
    const minute = Number(body.slice(hourLen));
    if (body.length === 4) {
      // Always a literal 24h HHMM — a leading zero or hour ≥ 13 only comes from someone
      // typing in 24-hour notation, never from an ambiguous "930"-style shorthand. A
      // trailing meridiem on a 4-digit run is ignored as meaningless (e.g. "1630pm").
      if (minute > 59 || hour > 23) return null;
      return hour * 60 + minute;
    }
    return applyMeridiem(hour, minute, meridiem, options.anchorMinutes);
  }

  const hourOnlyMatch = /^\d{1,2}$/.exec(body);
  if (hourOnlyMatch) {
    return applyMeridiem(Number(body), 0, meridiem, options.anchorMinutes);
  }

  return null;
}

/** `"HH:MM"` (24h, as stored on the draft) → minutes-since-midnight. */
export function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** minutes-since-midnight → `"HH:MM"` (24h, wraps into 0–1439 first). */
export function minutesToHhmm(minutes: number): string {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** minutes-since-midnight → friendly 12h label, e.g. `"4:30 PM"`. */
export function formatTimeLabel(minutes: number): string {
  const wrapped = ((minutes % 1440) + 1440) % 1440;
  const h24 = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

/** Short duration label for a non-negative gap in minutes, e.g. `90 -> "1h 30m"`, `45 -> "45m"`. */
export function formatDurationLabel(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
